import type { CaseImageRecord } from '@/api/caseImages'
import { caseImageForSlot } from '@/atoms/caseImageAtoms'
import { isImagePlaceholder } from '@/utils/editor/imagePlaceholder'

/** The parts of an editor image item that identify which case photo it shows. */
export type CaseImageItemRef = {
  image?: { url?: string }
  properties?: { caseImage?: number | string }
}

/**
 * Reduce an image URL to its bucket path (no origin, no query string) so a
 * URL the layout stored as a bare path compares equal to the same image
 * served with the bucket origin or a cache-busting query. Exported so every
 * place that decides "is this canvas item showing that case photo" compares
 * URLs the same way.
 */
export const imagePath = (url: string): string =>
  url
    .replace(/^https?:\/\/[^/]+/, '')
    .replace(/\?.*$/, '')
    .replace(/^\/+/, '')

/**
 * Find the CaseImage row (the uploaded photo record with its id) that an
 * editor image item is displaying. Two lookup rules mirror how the editor
 * fills images:
 *
 * - An item whose url is a real image was swapped or filled to a specific
 *   photo, so it must match a record by URL.
 * - An item whose url is still the template placeholder resolves by its
 *   `properties.caseImage` slot index through `caseImageForSlot`, the same
 *   wrap-around rule the filled-images list uses.
 *
 * Returns null when nothing matches, which callers use to hide tools that
 * need a real case photo (for example AI background removal).
 */
export const resolveCaseImageForItem = (
  images: CaseImageRecord[],
  item: CaseImageItemRef | null | undefined,
): CaseImageRecord | null => {
  if (!item || images.length === 0) return null

  const url = item.image?.url
  if (url && !isImagePlaceholder(url)) {
    const wanted = imagePath(url)
    return (
      images.find((img) =>
        [img.displayUrl, img.originalUrl, img.mioUrl].some(
          (candidate) => candidate && imagePath(candidate) === wanted,
        ),
      ) ?? null
    )
  }

  const rawIndex = item.properties?.caseImage
  const slotIndex =
    typeof rawIndex === 'string' ? parseInt(rawIndex, 10) : (rawIndex ?? 0)
  return caseImageForSlot(images, slotIndex)
}
